import React from 'react';
import { useNavigate } from 'react-router-dom';

export const ServicePausedPage = () => {
  const navigate = useNavigate();

  return (
    <div className="w-full min-h-screen flex items-center justify-center px-4 sm:px-8 relative overflow-hidden font-sans bg-zinc-950">
      <style>{`
        @keyframes springIn {
          0% { opacity: 0; transform: translateY(16px) scale(0.97); }
          100% { opacity: 1; transform: translateY(0) scale(1); }
        }

        @keyframes pulseGlow {
          0%, 100% { opacity: 0.15; transform: scale(1); }
          50% { opacity: 0.35; transform: scale(1.08); }
        }

        @keyframes borderGlow {
          0%, 100% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
        }

        @keyframes floatY {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-8px); }
        }

        @keyframes ringPulse {
          0% { transform: scale(0.9); opacity: 0.6; }
          70% { transform: scale(1.6); opacity: 0; }
          100% { transform: scale(1.6); opacity: 0; }
        }

        @keyframes shimmer {
          0% { background-position: 200% 0; }
          100% { background-position: -200% 0; }
        }

        .animate-spring-in { animation: springIn 0.5s cubic-bezier(0.16, 1, 0.3, 1) forwards; }
        .ambient-glow { animation: pulseGlow 8s ease-in-out infinite; }
        .float-y { animation: floatY 4s ease-in-out infinite; }

        .highlight-border {
          background: linear-gradient(135deg, rgba(16, 185, 129, 0.4) 0%, rgba(24, 24, 27, 0.8) 50%, rgba(20, 184, 166, 0.4) 100%);
          background-size: 200% 200%;
          animation: borderGlow 6s ease infinite;
        }

        .micro-interaction { transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1); }
        .micro-interaction:hover { transform: translateY(-2px); }
        .micro-interaction:active { transform: translateY(0px) scale(0.98); }

        .ring-pulse::before,
        .ring-pulse::after {
          content: '';
          position: absolute;
          inset: 0;
          border-radius: 9999px;
          border: 2px solid rgba(16, 185, 129, 0.5);
          animation: ringPulse 2.8s cubic-bezier(0.4, 0, 0.6, 1) infinite;
        }
        .ring-pulse::after { animation-delay: 1.4s; }

        .shimmer-text {
          background: linear-gradient(90deg, #a1a1aa 0%, #10b981 50%, #a1a1aa 100%);
          background-size: 200% auto;
          -webkit-background-clip: text;
          background-clip: text;
          -webkit-text-fill-color: transparent;
          animation: shimmer 4s linear infinite;
        }
      `}</style>

      {/* Ambient background blur circles */}
      <div className="absolute top-10 left-1/4 w-96 h-96 bg-emerald-500/10 rounded-full blur-[120px] pointer-events-none ambient-glow" />
      <div className="absolute bottom-10 right-1/4 w-80 h-80 bg-teal-500/10 rounded-full blur-[100px] pointer-events-none ambient-glow" />
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-emerald-500/5 rounded-full blur-[140px] pointer-events-none ambient-glow" />

      <div className="relative z-10 max-w-lg w-full">
        {/* Card with animated gradient border */}
        <div className="relative p-[1px] rounded-[2.5rem] highlight-border shadow-2xl animate-spring-in">
          <div className="bg-zinc-900/95 backdrop-blur-xl p-8 sm:p-10 rounded-[2.45rem] text-center space-y-6 relative overflow-hidden">

            {/* Icon with pulsing rings */}
            <div className="relative w-20 h-20 mx-auto float-y">
              <div className="absolute inset-0 rounded-full ring-pulse" />
              <div className="relative w-20 h-20 rounded-full bg-zinc-950 border border-emerald-500/30 flex items-center justify-center shadow-lg shadow-emerald-500/10">
                <svg className="w-9 h-9 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 9v6m4-6v6m7-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
            </div>

            {/* Badge */}
            <span className="inline-block px-3 py-1 bg-emerald-500/10 text-emerald-400 border border-emerald-500/30 rounded-lg text-[10px] font-bold uppercase tracking-wider shadow-sm shadow-emerald-500/10">
              Service Temporarily Paused
            </span>

            {/* Heading */}
            <div className="space-y-2">
              <h1 className="text-2xl sm:text-3xl font-black tracking-tight text-zinc-100">
                We'll Be Right Back
              </h1>
              <p className="text-zinc-400 text-sm font-medium leading-relaxed max-w-sm mx-auto">
                This service is currently <span className="shimmer-text font-bold">paused or unavailable</span>. Our team is working behind the scenes — please check back shortly.
              </p>
            </div>

            {/* Divider */}
            <div className="h-px w-full bg-gradient-to-r from-transparent via-zinc-700/60 to-transparent" />

            {/* Status info card */}
            <div className="flex items-center justify-center gap-6 text-[11px] font-bold uppercase tracking-wider text-zinc-500">
              <span className="flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full bg-amber-400 animate-pulse" />
                Under Maintenance
              </span>
              <span className="w-1 h-1 rounded-full bg-zinc-700" />
              <span>Auto-Retry Enabled</span>
            </div>

            {/* Actions */}
            <div className="flex flex-col sm:flex-row gap-3 pt-2">
              <button
                onClick={() => window.location.reload()}
                className="flex-1 flex items-center justify-center gap-2 py-3.5 bg-emerald-500 hover:bg-emerald-400 text-zinc-950 rounded-xl text-xs font-black shadow-lg shadow-emerald-500/20 transition-all micro-interaction active:scale-95"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                Retry Now
              </button>
              <button
                onClick={() => navigate('/')}
                className="flex-1 flex items-center justify-center gap-2 py-3.5 bg-zinc-950 hover:bg-zinc-800 text-zinc-300 hover:text-zinc-100 rounded-xl text-xs font-bold border border-zinc-800 transition-all micro-interaction active:scale-95"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                </svg>
                Go Home
              </button>
            </div>

          </div>
        </div>

        {/* Footnote */}
        <p className="text-center text-[10px] font-bold uppercase tracking-widest text-zinc-600 mt-6 animate-spring-in">
          Need urgent help? Contact support
        </p>
      </div>
    </div>
  );
};

export default ServicePausedPage;